import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Settings, ArrowLeft, Move3D, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useClock } from '@/contexts/ClockContext';
import { DigitalClock } from '@/components/clock-faces/DigitalClock';
import { AnalogClock } from '@/components/clock-faces/AnalogClock';
import { ModernDigitalClock } from '@/components/clock-faces/ModernDigitalClock';
import { TimerDisplay } from '@/components/clock/TimerDisplay';
import { TimerAlarmNotification } from '@/components/clock/TimerAlarmNotification';
import { TimerAlarmSettings } from '@/components/clock/TimerAlarmSettings';
import { AlarmIndicator } from '@/components/clock/AlarmIndicator';
import { useTimerAlarmMonitor } from '@/hooks/useTimerAlarmMonitor';
import { GRADIENT_PRESETS, getGradientStyle } from '@/utils/clockGradients';
import { getTimeInZone, getUTCOffset } from '@/utils/timezones';
import type { ClockFaceStyle, GradientPreset, DigitalFontStyle, FontSize } from '@/types/clock';

const ClockWidgetInner = () => {
  const { settings, currentTime, addTimeZone, removeTimeZone, updateFormat, updateClockFace, updateDigitalFont, updateFontSize, updateGradient, toggleSeconds, toggleDate, toggleTimeZone, searchCities, addTimer, pauseTimer, resumeTimer, cancelTimer, addAlarm, toggleAlarm, deleteAlarm, snoozeAlarm } = useClock();
  
  // Calculate live timezone times based on currentTime
  const liveTimeZones = useMemo(() => {
    if (!settings?.timeZones || settings.timeZones.length === 0) {
      return [];
    }
    return settings.timeZones.map(tz => ({
      ...tz,
      time: getTimeInZone(tz.timezone),
      offset: getUTCOffset(tz.timezone)
    }));
  }, [settings?.timeZones, currentTime]); // Recalculate when currentTime updates
  
  const [currentCityIndex, setCurrentCityIndex] = useState(0);
  
  // Intelligent sizing based on clock face type
  const getSmartDimensions = () => {
    const style = settings?.clockFaceStyle || 'analog-luxury';
    if (style.startsWith('digital-compact')) {
      return { width: 450, height: 350 };
    }
    if (style.startsWith('digital-flip')) {
      return { width: 550, height: 400 };
    }
    if (style.startsWith('digital-')) {
      return { width: 480, height: 420 };
    }
    return { width: 520, height: 520 }; // Analog default
  };
  
  const [dimensions, setDimensions] = useState(getSmartDimensions());
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isFlipped, setIsFlipped] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCitySearch, setShowCitySearch] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'timer' | 'alarm'>('timer'); // Track which tab to open in settings
  const containerRef = useRef<HTMLDivElement>(null);

  const gradientConfig = GRADIENT_PRESETS[settings?.gradientPreset || 'ocean'];
  const gradientStyle = getGradientStyle(settings?.gradientPreset || 'ocean', settings?.customGradient);

  // Timer/Alarm monitoring
  const { activeTrigger, handleDismiss: handleNotificationDismiss, handleSnooze: handleSnoozeFromHook } = useTimerAlarmMonitor(
    settings?.timers || [],
    settings?.alarms || [],
    cancelTimer, // onTimerComplete
    (id) => { // onAlarmTriggered  
      // For alarms, just mark them as checked - they'll re-trigger based on repeat
      console.log('[Clock] Alarm triggered:', id);
    }
  );
  
  // Wrap snooze to pass label
  const handleSnooze = useCallback(() => {
    snoozeAlarm(activeTrigger?.label);
    handleSnoozeFromHook();
  }, [snoozeAlarm, activeTrigger, handleSnoozeFromHook]);

  // Get active timer (first running timer)
  const activeTimer = useMemo(() => {
    return settings?.timers?.find(t => t.running);
  }, [settings?.timers]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({ x: e.clientX, y: e.clientY, width: dimensions.width, height: dimensions.height });
  };

  const handleResizeEnd = () => setIsResizing(false);

  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizing) return;
    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;
    
    // Smart min/max based on clock face
    const isDigital = settings?.clockFaceStyle?.startsWith('digital-');
    const minW = isDigital ? 350 : 400;
    const minH = isDigital ? 280 : 400;
    const maxW = isDigital ? 800 : 700;
    const maxH = isDigital ? 600 : 700;
    
    const newWidth = Math.max(minW, Math.min(maxW, resizeStart.width + deltaX));
    const newHeight = Math.max(minH, Math.min(maxH, resizeStart.height + deltaY));
    setDimensions({ width: newWidth, height: newHeight });
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeStart]);
  
  // Keyboard navigation for cities
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (isFlipped) return; // Don't navigate when settings are open
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevCity();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextCity();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFlipped, liveTimeZones.length, currentCityIndex]);

  const handleCitySelect = (cityString: string) => {
    const results = searchCities(searchQuery);
    const selected = results.find(r => `${r.city}, ${r.country}` === cityString);
    if (selected) {
      addTimeZone(selected.city, selected.timezone);
      setSearchQuery('');
      setShowCitySearch(false);
    }
  };

  const searchResults = searchQuery.length > 1 ? searchCities(searchQuery) : [];
  
  // City navigation
  const goToNextCity = () => {
    if (liveTimeZones.length <= 1) return;
    setIsTransitioning(true);
    setCurrentCityIndex((prev) => (prev + 1) % liveTimeZones.length);
    setTimeout(() => setIsTransitioning(false), 300);
  };
  
  const goToPrevCity = () => {
    if (liveTimeZones.length <= 1) return;
    setIsTransitioning(true);
    setCurrentCityIndex((prev) => (prev - 1 + liveTimeZones.length) % liveTimeZones.length);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const renderClock = () => {
    if (!liveTimeZones || liveTimeZones.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className={`text-lg ${gradientConfig.textColor}`}>Loading...</div>
        </div>
      );
    }

    const currentCity = liveTimeZones[currentCityIndex];
    if (!currentCity) return null;

    const isAnalog = settings.clockFaceStyle.startsWith('analog-');
    const isModernDigital = ['digital-minimal', 'digital-compact', 'digital-bold', 'digital-neon', 'digital-flip', 'digital-segmented'].includes(settings.clockFaceStyle);
    const textColor = gradientConfig.textColor.replace('text-', '');

    if (isAnalog) {
      const style = settings.clockFaceStyle.replace('analog-', '') as 'luxury' | 'minimal' | 'swiss' | 'deco';
      return (
        <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          <AnalogClock
            time={currentCity.time}
            showSeconds={settings.showSeconds}
            showDate={settings.showDate}
            showTimeZone={settings.showTimeZone}
            timeZoneName={currentCity.city}
            textColor={textColor === 'white' ? '#ffffff' : textColor === 'gray-800' ? '#1f2937' : textColor}
            accentColor={gradientConfig.accentColor}
            style={style}
          />
        </div>
      );
    }

    if (isModernDigital) {
      return (
        <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          <ModernDigitalClock
            time={currentCity.time}
            format={settings.format}
            showSeconds={settings.showSeconds}
            showDate={settings.showDate}
            showTimeZone={settings.showTimeZone}
            timeZoneName={currentCity.city}
            offset={currentCity.offset}
            textColor={gradientConfig.textColor}
            accentColor={gradientConfig.accentColor}
            style={settings.clockFaceStyle}
            fontStyle={settings.digitalFontStyle}
            fontSize={settings.fontSize}
          />
        </div>
      );
    }

    return (
      <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <DigitalClock
          time={currentCity.time}
          format={settings.format}
          showSeconds={settings.showSeconds}
          showDate={settings.showDate}
          showTimeZone={settings.showTimeZone}
          timeZoneName={currentCity.city}
          offset={currentCity.offset}
          textColor={gradientConfig.textColor}
        />
      </div>
    );
  };

  return (
    <div 
      style={{ width: `${dimensions.width}px`, height: `${dimensions.height}px`, perspective: '1000px' }}
      className="relative"
    >
      <div 
        ref={containerRef}
        className="w-full h-full relative transition-transform duration-700"
        style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
      >
        {/* Front Side - Clock Display */}
        <div 
          className="absolute inset-0 rounded-3xl shadow-2xl border border-white/10 overflow-hidden backdrop-blur-xl"
          style={{ backfaceVisibility: 'hidden', background: gradientStyle }}
        >
          {/* Settings button */}
          <button
            onClick={() => setIsFlipped(true)}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 backdrop-blur-sm border border-white/20 group"
          >
            <Settings className={`w-5 h-5 ${gradientConfig.textColor} group-hover:rotate-90 transition-transform duration-300`} />
          </button>
          
          {/* Alarm Indicator Badge */}
          {!activeTimer && (
            <AlarmIndicator
              alarms={settings?.alarms || []}
              onClickNavigate={() => {
                setSettingsTab('alarm');
                setIsFlipped(true);
              }}
              accentColor={gradientConfig.accentColor}
            />
          )}

          <div
            onMouseDown={handleResizeStart}
            className="absolute bottom-3 right-3 w-6 h-6 cursor-se-resize opacity-40 hover:opacity-80 transition-all duration-300 z-50 widget-nodrag"
          >
            <Move3D className={`w-5 h-5 ${gradientConfig.textColor} rotate-45`} />
          </div>

          <div className="relative z-10 h-full flex flex-col">
            {renderClock()}
            
            {/* Active Timer Overlay */}
            {activeTimer && (
              <TimerDisplay
                timer={activeTimer}
                onPause={() => pauseTimer(activeTimer.id)}
                onResume={() => resumeTimer(activeTimer.id)}
                onCancel={() => cancelTimer(activeTimer.id)}
                accentColor={gradientConfig.accentColor}
                textColor={gradientConfig.textColor}
              />
            )}
            
            {/* City Navigation - Only show if multiple cities */}
            {liveTimeZones.length > 1 && (
              <>
                {/* Previous City Button */}
                <button
                  onClick={goToPrevCity}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all duration-200 hover:scale-110 group"
                  title="Previous city"
                >
                  <ChevronLeft className={`w-6 h-6 ${gradientConfig.textColor} group-hover:scale-110 transition-transform`} />
                </button>
                
                {/* Next City Button */}
                <button
                  onClick={goToNextCity}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all duration-200 hover:scale-110 group"
                  title="Next city"
                >
                  <ChevronRight className={`w-6 h-6 ${gradientConfig.textColor} group-hover:scale-110 transition-transform`} />
                </button>
                
                {/* City Indicator Dots */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                  {liveTimeZones.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setIsTransitioning(true);
                        setCurrentCityIndex(index);
                        setTimeout(() => setIsTransitioning(false), 300);
                      }}
                      className={`transition-all duration-300 rounded-full ${
                        index === currentCityIndex
                          ? 'w-8 h-2 bg-white/80'
                          : 'w-2 h-2 bg-white/30 hover:bg-white/50'
                      }`}
                      title={liveTimeZones[index]?.city}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Back Side - Settings Panel */}
        <div 
          className="absolute inset-0 rounded-3xl shadow-2xl border border-white/10 overflow-hidden backdrop-blur-xl bg-slate-900/95"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <button
            onClick={() => setIsFlipped(false)}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 backdrop-blur-sm border border-white/20 group"
          >
            <ArrowLeft className="w-5 h-5 text-white group-hover:-translate-x-1 transition-transform duration-300" />
          </button>

          <div className="relative z-10 h-full p-6 flex flex-col overflow-y-auto">
            <h3 className="text-white font-semibold text-xl mb-6">Clock Settings</h3>
            
            {/* Watch Face Selection */}
            <div className="mb-6">
              <label className="block text-white/80 text-sm font-medium mb-3">Watch Face Style</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'analog-luxury', name: 'Luxury', icon: '👑', category: 'analog' },
                  { id: 'analog-minimal', name: 'Minimal', icon: '⚪', category: 'analog' },
                  { id: 'analog-swiss', name: 'Swiss', icon: '🇨🇭', category: 'analog' },
                  { id: 'analog-deco', name: 'Deco', icon: '💎', category: 'analog' },
                  { id: 'digital-minimal', name: 'Minimal', icon: '✨', category: 'digital' },
                  { id: 'digital-compact', name: 'Compact', icon: '📦', category: 'digital' },
                  { id: 'digital-bold', name: 'Bold', icon: '💪', category: 'digital' },
                  { id: 'digital-neon', name: 'Neon', icon: '🌟', category: 'digital' },
                  { id: 'digital-flip', name: 'Flip', icon: '🔢', category: 'digital' },
                  { id: 'digital-segmented', name: '7-Seg', icon: '🔴', category: 'digital' },
                ].map((face) => (
                  <button
                    key={face.id}
                    onClick={() => {
                      updateClockFace(face.id as ClockFaceStyle);
                      // Auto-resize based on new face type
                      setTimeout(() => setDimensions(getSmartDimensions()), 50);
                    }}
                    className={`p-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                      settings.clockFaceStyle === face.id
                        ? 'bg-blue-500/30 text-white border-2 border-blue-400/50 scale-105'
                        : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="text-lg mb-0.5">{face.icon}</div>
                    {face.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Digital Font Customization - Only show for modern digital faces */}
            {['digital-minimal', 'digital-compact', 'digital-bold', 'digital-neon', 'digital-flip', 'digital-segmented'].includes(settings.clockFaceStyle) && (
              <>
                <div className="mb-6">
                  <label className="block text-white/80 text-sm font-medium mb-3">Typography</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'inter', name: 'Satoshi', icon: 'Aa', desc: 'Geometric' },
                      { id: 'mono', name: 'Silkscreen', icon: 'Tt', desc: 'Pixel' },
                      { id: 'display', name: 'Clash', icon: 'Dd', desc: 'Bold' },
                      { id: 'condensed', name: 'Switzer', icon: 'Cc', desc: 'Swiss' },
                      { id: 'rounded', name: 'Chillax', icon: 'Rr', desc: 'Rounded' },
                    ].map((font) => (
                      <button
                        key={font.id}
                        onClick={() => updateDigitalFont(font.id as DigitalFontStyle)}
                        className={`p-2 rounded-lg text-xs font-medium transition-all ${
                          settings.digitalFontStyle === font.id
                            ? 'bg-blue-500/30 text-white border border-blue-400/50'
                            : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="text-base font-bold mb-0.5">{font.icon}</div>
                        {font.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-white/80 text-sm font-medium mb-3">Font Size</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: 'small', name: 'Small', icon: 'S' },
                      { id: 'medium', name: 'Medium', icon: 'M' },
                      { id: 'large', name: 'Large', icon: 'L' },
                      { id: 'extra-large', name: 'XL', icon: 'XL' },
                    ].map((size) => (
                      <button
                        key={size.id}
                        onClick={() => updateFontSize(size.id as FontSize)}
                        className={`p-2 rounded-lg text-xs font-medium transition-all ${
                          settings.fontSize === size.id
                            ? 'bg-blue-500/30 text-white border border-blue-400/50'
                            : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="font-bold mb-0.5">{size.icon}</div>
                        {size.name}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Gradient Selection */}
            <div className="mb-6">
              <label className="block text-white/80 text-sm font-medium mb-3">Background Gradient</label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(GRADIENT_PRESETS) as GradientPreset[]).filter(k => k !== 'custom').map((preset) => (
                  <button
                    key={preset}
                    onClick={() => updateGradient(preset)}
                    className={`relative h-12 rounded-lg overflow-hidden border-2 transition-all ${
                      settings.gradientPreset === preset ? 'border-white/50 scale-105' : 'border-white/10 hover:border-white/30'
                    }`}
                    style={{ background: GRADIENT_PRESETS[preset].gradient }}
                    title={GRADIENT_PRESETS[preset].name}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-medium text-white drop-shadow-lg">{GRADIENT_PRESETS[preset].name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Time Format */}
            <div className="mb-6">
              <label className="block text-white/80 text-sm font-medium mb-3">Time Format</label>
              <div className="flex gap-2">
                {['12h', '24h'].map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => updateFormat(fmt as '12h' | '24h')}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      settings.format === fmt
                        ? 'bg-blue-500/30 text-white border border-blue-400/50'
                        : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {fmt === '12h' ? '12-hour' : '24-hour'}
                  </button>
                ))}
              </div>
            </div>

            {/* Display Options */}
            <div className="mb-6">
              <label className="block text-white/80 text-sm font-medium mb-3">Display</label>
              <div className="space-y-2">
                {[
                  { key: 'showSeconds', label: 'Show Seconds', action: toggleSeconds },
                  { key: 'showDate', label: 'Show Date', action: toggleDate },
                  { key: 'showTimeZone', label: 'Show Timezone', action: toggleTimeZone }
                ].map(({ key, label, action }) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer group">
                    <input type="checkbox" checked={settings[key as keyof typeof settings] as boolean} onChange={action} className="sr-only" />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      settings[key as keyof typeof settings] ? 'bg-blue-500/30 border-blue-400/50' : 'border-white/30 group-hover:border-white/50'
                    }`}>
                      {settings[key as keyof typeof settings] && <div className="w-2.5 h-2.5 bg-blue-400 rounded-sm"></div>}
                    </div>
                    <span className="text-sm text-white/80 font-medium">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Timer & Alarm Settings */}
            <div className="mb-6">
              <TimerAlarmSettings
                timers={settings?.timers || []}
                alarms={settings?.alarms || []}
                onAddTimer={addTimer}
                onCancelTimer={cancelTimer}
                onAddAlarm={(time, repeat, label) => addAlarm(time, repeat, label)}
                onToggleAlarm={toggleAlarm}
                onDeleteAlarm={deleteAlarm}
                accentColor={gradientConfig.accentColor}
                initialTab={settingsTab}
              />
            </div>

            {/* Add Cities */}
            <div className="mb-4">
              <label className="block text-white/80 text-sm font-medium mb-3">World Clocks</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowCitySearch(true)}
                  onBlur={() => setTimeout(() => setShowCitySearch(false), 200)}
                  placeholder="Search cities..."
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-blue-400/50"
                />
                {showCitySearch && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800/95 border border-white/20 rounded-lg max-h-48 overflow-y-auto z-50">
                    {searchResults.map((result) => (
                      <button
                        key={`${result.city}-${result.timezone}`}
                        onClick={() => handleCitySelect(`${result.city}, ${result.country}`)}
                        className="w-full px-4 py-2 text-left text-white/90 hover:bg-white/10 transition-colors"
                      >
                        {result.city}, {result.country}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Current Timezones */}
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {liveTimeZones.map((tz) => (
                  <div key={tz.id} className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/10">
                    <div>
                      <div className="text-sm font-medium text-white">{tz.city}</div>
                      <div className="text-xs text-white/50">{tz.offset}</div>
                    </div>
                    {settings.timeZones.length > 1 && (
                      <button onClick={() => removeTimeZone(tz.id)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-500/20 transition-colors text-white/60 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Timer/Alarm Notification Modal (renders outside widget) */}
      {activeTrigger && (
        <TimerAlarmNotification
          type={activeTrigger.type}
          label={activeTrigger.label}
          onDismiss={handleNotificationDismiss}
          onSnooze={activeTrigger.type === 'alarm' ? handleSnooze : undefined}
          accentColor={gradientConfig.accentColor}
        />
      )}
    </div>
  );
};

export const ClockWidget = () => {
  try {
    return <ClockWidgetInner />;
  } catch (error) {
    console.error('[ClockWidget] Render error:', error);
    return (
      <div className="flex items-center justify-center w-full h-full bg-red-500/10 border border-red-500/30 rounded-3xl">
        <div className="text-white text-center p-6">
          <div className="text-xl font-bold mb-2">Clock Widget Error</div>
          <div className="text-sm opacity-75">Please try again</div>
        </div>
      </div>
    );
  }
};
